import { apiGet, apiPost } from './client';
import { ApiResponse } from '@tinyrewards/shared';

/** Shape returned by `/api/youtube/details`. */
export interface YoutubeDetails {
    title:        string;
    authorName:   string;
    thumbnailUrl: string;
    html:         string;
}

/** One transcript segment (offsets in milliseconds). */
export interface TranscriptSegment {
    offset:   number;
    duration?: number;
    text:     string;
}

/** Wrapper shape Supadata sometimes returns. */
interface TranscriptEnvelope {
    content?: TranscriptSegment[];
    transcript?: TranscriptSegment[];
}

/** A single timed question, matching the extension's Quiz modal contract. */
export interface TimedQuestionOption {
    answer:    string;
    isCorrect: boolean;
    id?:       string;
}

export interface TimedQuestion {
    id:           string;
    questionText: string;
    options:      TimedQuestionOption[];
    timeStamp:    string | null;   // "HH:MM:SS" or null when untimed
    feedback:     { correct: string; incorrect: string };
}

export function getYoutubeDetails(url: string): Promise<ApiResponse<YoutubeDetails>> {
    return apiGet<YoutubeDetails>(`/api/youtube/details?url=${encodeURIComponent(url)}`);
}

/**
 * Extract the 11-char YouTube videoId from any common URL shape (or accept
 * a bare videoId). Returns null when nothing matches.
 */
export function extractYouTubeId(input: string): string | null {
    if (!input) return null;
    const trimmed = input.trim();
    if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
    const m = trimmed.match(/(?:v=|youtu\.be\/|\/embed\/|\/v\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] ?? null : null;
}

/**
 * Fetch the transcript for a YouTube videoId. Supadata occasionally wraps
 * its segment array in `content`/`transcript`; normalize to a flat array.
 */
export async function loadYoutubeTranscript(
    videoId: string,
): Promise<ApiResponse<TranscriptSegment[]>> {
    const res = await apiPost<TranscriptSegment[] | TranscriptEnvelope | string>(
        '/api/youtube/transcript',
        { videoId },
    );
    if (!res.success || res.data === undefined) return res as ApiResponse<TranscriptSegment[]>;
    const raw = res.data;
    let segments: TranscriptSegment[] = [];
    if (Array.isArray(raw)) {
        segments = raw as TranscriptSegment[];
    } else if (typeof raw === 'object' && raw !== null) {
        const env = raw as TranscriptEnvelope;
        segments = env.content ?? env.transcript ?? [];
    }
    return { success: true, data: segments };
}

/**
 * Run the LLM question generator against a transcript (text or structured
 * segments). When `transcript` is a segment array, each returned question
 * carries a `timeStamp` in "HH:MM:SS".
 */
export function generateTimedQuestions(args: {
    transcript:            string | TranscriptSegment[];
    count?:                number;
    videoDurationSeconds?: number;
}): Promise<ApiResponse<{ questions: TimedQuestion[] }>> {
    return apiPost<{ questions: TimedQuestion[] }>('/api/youtube/questions', args);
}
